import React, { useContext, useEffect, useState } from 'react';
import { DataContext } from '../context/DataContext';
import { TradingData } from '../helpers/CSVParser';
import TradingMetricsCalculator from '../helpers/TradingMetricsCalculator';

import { Table, TableBody, TableCell, TableContainer, TableRow, Typography, Tooltip as MuiTooltip, useTheme, Paper } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import { LoadingContext } from '../context/LoadingContext';
import { SettingsContext } from '../context/SettingsContext';
import { TranslationContext, useTranslation } from '../context/TranslationContext';

interface GraphDataRow {
    period: string; totalNetProfit?: number, totalNetProfitIfPositive?: number, totalNetProfitIfNegative?: number, totalProfits?: number, totalLosses?: number
}
interface GraphData {
  title: string;
  data: GraphDataRow[];
  initialCapital?: number;
}

const MonthlyGraphs: React.FC = () => {
    const { dataset } = useContext(DataContext);
    const { loading, setLoading } = useContext(LoadingContext);
    const { collapsed, getLocale, showCurrencySymbol, getCurrencySymbol } = useContext(SettingsContext);
    const { t } = useTranslation();
    const { translateMonth } = useContext(TranslationContext);
    const [allDataGraph, setAllDataGraph] = useState<GraphData>();
    const [otherDataGraph, setOtherDataGraph] = useState<GraphData[]>([]);
    const [chartWidth, setChartWidth] = useState(window.innerWidth * (collapsed ? 0.45 : 0.35));
    const chartHeight = 300;
    const theme = useTheme();

    const formatValue = (value: number, decimals: number) => {
        const formattedValue = value.toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        if (showCurrencySymbol) {
            return `${formattedValue}\u00A0${getCurrencySymbol()}`;
        }
        return formattedValue;
    };

    const formatMonth = (period: string) => {
        return translateMonth(period);
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload as GraphDataRow;
            let netProfit = data.totalNetProfit ? data.totalNetProfit : 0;
            if (netProfit === 0 && data.totalProfits && data.totalLosses) netProfit = data.totalProfits + data.totalLosses; // backup for when netProfit is 0
            const totalWins = data.totalProfits ? data.totalProfits : 0;
            const totalLosses = data.totalLosses ? data.totalLosses : 0;

            return (
                <MuiTooltip title="" placement="top">
                    <Paper style={{ padding: '10px', border: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="subtitle1" component="strong">{formatMonth(label)}</Typography>
                        <p style={{ color: netProfit >= 0 ? theme.palette.success.main : theme.palette.error.main, margin: '4px 0' }}>
                            {`${t('Net Profit')}: ${formatValue(netProfit, 2)}`}
                        </p>
                        <hr/>
                        <p style={{ color: theme.palette.success.main, margin: '4px 0' }}>
                            {`${t('Total Wins')}: ${formatValue(totalWins, 2)}`}
                        </p>
                        <p style={{ color: theme.palette.error.main, margin: '4px 0' }}>
                            {`${t('Total Losses')}: ${formatValue(totalLosses, 2)}`}
                        </p>
                    </Paper>
                </MuiTooltip>
            );
        }
        return null;
    };

    useEffect(() => {
        const handleResize = () => {
            const factor = collapsed ? 0.45 : 0.35;
            setChartWidth(window.innerWidth * factor);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const factor = collapsed ? 0.42 : 0.35;
        setChartWidth(window.innerWidth * factor);
    }, [collapsed]);

    useEffect(() => {
        setLoading(true);
        setTimeout(() => { // Simulate a delay for the loading spinner
            const filesToConsider = dataset.files.filter(file => file.include === true);
            if (filesToConsider.length === 0) {
                setLoading(false);
                return;
            }
            let initialCapital = 0;
            let allTrades: TradingData[] = [];
            dataset.files.filter(file => file.include === true).forEach(file => {
                initialCapital += file.initialCapital;
                // filter parsedData by customStartDate and customEndDate if useAllTrades is false
                let filteredData = file.parsedData.filter(trade => trade.type.toLowerCase().includes('exit'));
                if (dataset.useDeepBacktest && dataset.customStartDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customStartDate ? tradeDate >= new Date(dataset.customStartDate) : false;
                    });
                }
                if (dataset.useDeepBacktest && dataset.customEndDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customEndDate ? tradeDate <= new Date(dataset.customEndDate) : false;
                    });
                }
                allTrades = [...allTrades, ...filteredData];
            });

            allTrades = TradingMetricsCalculator.recalculateTradeList(allTrades, initialCapital);

            const accumulatedMetrics = new TradingMetricsCalculator(allTrades, true, initialCapital).calculateMonthlyMetrics();
            const otherDataGraphs: GraphData[] = [];

            dataset.files.forEach(file => {
                if (file.include) {
                    // filter parsedData by customStartDate and customEndDate if useAllTrades is false
                    let filteredData = file.parsedData;
                    if (dataset.useDeepBacktest && dataset.customStartDate) {
                        filteredData = filteredData.filter(trade => {
                            const tradeDate = new Date(trade.datetime);
                            return dataset.customStartDate ? tradeDate >= new Date(dataset.customStartDate) : false;
                        });
                    }
                    if (dataset.useDeepBacktest && dataset.customEndDate) {
                        filteredData = filteredData.filter(trade => {
                            const tradeDate = new Date(trade.datetime);
                            return dataset.customEndDate ? tradeDate <= new Date(dataset.customEndDate) : false;
                        });
                    }
                    const trades = TradingMetricsCalculator.recalculateTradeList(filteredData, file.initialCapital);
                    const otherMetric = new TradingMetricsCalculator(trades, true, initialCapital).calculateMonthlyMetrics();
                    otherDataGraphs.push({
                        title: file.customId,
                        data: otherMetric.map(monthlyMetric => ({ period: monthlyMetric.period, totalNetProfitIfPositive: monthlyMetric.netProfitIfPositive, totalNetProfitIfNegative: monthlyMetric.netProfitIfNegative, totalProfits: monthlyMetric.totalProfits, totalLosses: monthlyMetric.totalLosses })),
                        initialCapital: file.initialCapital
                    });
                }
            });

            setAllDataGraph({
                title: `${t('All')}`,
                data: accumulatedMetrics.map(monthlyMetric => ({ 
                    period: monthlyMetric.period, 
                    totalNetProfit: monthlyMetric.netProfit, 
                    totalNetProfitIfPositive: monthlyMetric.netProfitIfPositive, 
                    totalNetProfitIfNegative: monthlyMetric.netProfitIfNegative, 
                    totalProfits: monthlyMetric.totalProfits, 
                    totalLosses: monthlyMetric.totalLosses 
                })),
                initialCapital: initialCapital
            });
            setOtherDataGraph(otherDataGraphs);
            setLoading(false);
        }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataset]);

    const tableRowHeight = '400px';

    if (loading) return <></>;
    return (
        <>
            <Typography variant="h4" gutterBottom>
                {t('Monthly Profit and Loss')}
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableBody>
                        <TableRow style={{ height: tableRowHeight }}>
                            <TableCell style={{ width: '50%' }}>
                                <Typography variant="h6" gutterBottom style={{ height: '32px' }}>
                                    {allDataGraph?.title}
                                </Typography>
                                <Paper elevation={3} style={{ padding: '16px', height: '100%', background: 'linear-gradient(to bottom,rgba(224, 247, 233, 0.04),rgba(26, 83, 48, 0.02))' }}>
                                    <BarChart
                                        data={allDataGraph?.data}
                                        height={chartHeight}
                                        width={chartWidth}
                                        stackOffset="sign"
                                        margin={{
                                            top: 0,
                                            right: 0,
                                            left: 35,
                                            bottom: 0,
                                        }}      
                                    >
                                        <defs>
                                            <linearGradient id="colorProfits" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#4caf50" stopOpacity={0.6} />
                                                <stop offset="95%" stopColor="#4caf50" stopOpacity={0.25} />
                                            </linearGradient>
                                            <linearGradient id="colorLosses" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f44336" stopOpacity={0.6} />
                                                <stop offset="95%" stopColor="#f44336" stopOpacity={0.25} />
                                            </linearGradient>
                                        </defs>

                                        <XAxis dataKey="period" tickFormatter={formatMonth} />
                                        <YAxis tickFormatter={(tick) => formatValue(tick, 2)} />
                                        <ReferenceLine y={0} stroke="rgba(250,250,250,0.1)" />
                                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }}/>
                                        <Bar dataKey="totalNetProfitIfPositive" fill="url(#colorProfits)" stackId="stack" />
                                        <Bar dataKey="totalNetProfitIfNegative" fill="url(#colorLosses)" stackId="stack" />
                                    </BarChart>
                                </Paper>
                            </TableCell>
                            <TableCell style={{ width: '50%' }}>
                                <Typography variant="h6" gutterBottom style={{ height: '32px' }}>
                                    {`${t('Profits and Losses')}`}
                                </Typography>
                                <Paper elevation={3} style={{ padding: '16px', height: '100%', background: 'linear-gradient(to bottom,rgba(224, 247, 233, 0.04),rgba(26, 83, 48, 0.02))' }}>
                                    <BarChart
                                        data={allDataGraph?.data}
                                        height={chartHeight}
                                        width={chartWidth}
                                        stackOffset="sign"
                                        margin={{
                                            top: 0,
                                            right: 0,
                                            left: 35,
                                            bottom: 0,
                                        }}      
                                    >
                                        <defs>
                                            <linearGradient id="colorProfits" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#4caf50" stopOpacity={0.6} />
                                                <stop offset="95%" stopColor="#4caf50" stopOpacity={0.25} />
                                            </linearGradient>
                                            <linearGradient id="colorLosses" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f44336" stopOpacity={0.6} />
                                                <stop offset="95%" stopColor="#f44336" stopOpacity={0.25} />
                                            </linearGradient>
                                        </defs>

                                        <XAxis dataKey="period" tickFormatter={formatMonth} />
                                        <YAxis tickFormatter={(tick) => formatValue(tick, 2)} />
                                        <ReferenceLine y={0} stroke="rgba(250,250,250,0.1)" />
                                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }}/>
                                        <Bar dataKey="totalProfits" fill="url(#colorProfits)" stackId="stack" />
                                        <Bar dataKey="totalLosses" fill="url(#colorLosses)" stackId="stack" />
                                    </BarChart>
                                </Paper>
                            </TableCell>
                        </TableRow>

                        {otherDataGraph.map((graphData, index) => (
                            <TableRow key={index} style={{ height: tableRowHeight }}>
                                <TableCell style={{ width: '50%' }}>
                                    <Typography variant="h6" gutterBottom style={{ height: '32px' }}>
                                        {graphData.title}
                                    </Typography>
                                    <Paper elevation={3} style={{ padding: '16px', height: '100%', background: 'linear-gradient(to bottom,rgba(224, 247, 233, 0.04),rgba(26, 83, 48, 0.02))' }}>
                                        <BarChart
                                            data={graphData.data}
                                            height={chartHeight}
                                            width={chartWidth}
                                            stackOffset="sign"
                                            margin={{
                                                top: 0,
                                                right: 0,
                                                left: 20,
                                                bottom: 0,
                                            }}      
                                        >
                                            <defs>
                                                <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#089981" stopOpacity={0.6} />
                                                    <stop offset="95%" stopColor="#089981" stopOpacity={0.25} />
                                                </linearGradient>
                                                <linearGradient id="colorRed" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#f44336" stopOpacity={0.6} />
                                                    <stop offset="95%" stopColor="#f44336" stopOpacity={0.25} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="period" tickFormatter={formatMonth} />
                                            <YAxis tickFormatter={(tick) => formatValue(tick, 2)} />
                                            <ReferenceLine y={0} stroke="rgba(250,250,250,0.1)" />
                                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }}/>
                                            <Bar dataKey="totalNetProfitIfPositive" fill="url(#colorProfits)" stackId="stack"/>
                                            <Bar dataKey="totalNetProfitIfNegative" fill="url(#colorLosses)" stackId="stack" />
                                        </BarChart>
                                    </Paper>
                                </TableCell>
                                <TableCell style={{ width: '50%' }}>
                                    <Typography variant="h6" gutterBottom style={{ height: '32px' }}>
                                        {`${t('Profits and Losses')}`}
                                    </Typography>
                                    <Paper elevation={3} style={{ padding: '16px', height: '100%', background: 'linear-gradient(to bottom,rgba(224, 247, 233, 0.04),rgba(26, 83, 48, 0.02))' }}>
                                        <BarChart
                                            data={otherDataGraph[index]?.data}
                                            height={chartHeight}
                                            width={chartWidth}
                                            stackOffset="sign"
                                            margin={{
                                                top: 0,
                                                right: 0,
                                                left: 20,
                                                bottom: 0,
                                            }}      
                                        >
                                            <defs>
                                                <linearGradient id="colorProfits" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.6} />
                                                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0.25} />
                                                </linearGradient>
                                                <linearGradient id="colorLosses" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#f44336" stopOpacity={0.6} />
                                                    <stop offset="95%" stopColor="#f44336" stopOpacity={0.25} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="period" tickFormatter={formatMonth} />
                                            <YAxis tickFormatter={(tick) => formatValue(tick, 2)} />
                                            <ReferenceLine y={0} stroke="rgba(250,250,250,0.1)" />
                                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }}/>
                                            <Bar dataKey="totalProfits" fill="url(#colorProfits)" stackId="stack"/>
                                            <Bar dataKey="totalLosses" fill="url(#colorLosses)" stackId="stack" />
                                        </BarChart>
                                    </Paper>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

export default MonthlyGraphs;
